import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { selectOrderState, selectIsValid } from '@store/order/selectors';
import { resetStore } from '@store/order/slice';
import { Button } from 'react-aria-components';
import { useSaveCustomerOrderMutation } from '@api/baseApi';
import { AppLanguageCode } from '@project-types/AppLanguageCodes';
import { convertToCustomerOrderDef, convertToOrderPurchase } from '@store/order/convertors';
import { NavigateRoutes } from '@features/routing/routesObject';
import { purchase } from '@features/gtm/gtmEvents';
import LoadingSpinner from '@components/loading-spinner/LoadingSpinner';
import { PricingModel } from '@api/types/PricingModel';



interface SaveButtonProps {
	price: PricingModel | null | undefined,
	country: AppLanguageCode,
}


const SaveButton = (props: SaveButtonProps) => {
	const { price, country } = props;
	const { t } = useTranslation([ 'forms', 'global' ]);
	
	const orderState = useSelector(selectOrderState);
	const isValid = useSelector(selectIsValid);
	const navigate = useNavigate();
	
	const dispatch = useDispatch();
	const runResetStore = () => dispatch(resetStore());
	const [ saveCustomerOrder, { data, isError, isLoading, isSuccess } ] = useSaveCustomerOrderMutation();


	const handleSave = useCallback(() => {
		if (!isValid || !orderState) {
			return;
		}
		
		let convertedObject = convertToCustomerOrderDef(orderState);
		if (!convertedObject) return;
		
		saveCustomerOrder(convertedObject);
	},[ isValid, orderState ]);


	useEffect(() => {
		if (data && isSuccess && price) {
			
			let convertedGtmObject = convertToOrderPurchase(orderState, price, country);
			purchase(convertedGtmObject);
			runResetStore();

			if (!data.isComgate) {
				navigate(NavigateRoutes.thankYouOrder);
				return;
			}
				
			data.comgateUrl
			? window.location.replace(data.comgateUrl)
			: navigate(NavigateRoutes.basePath);
		}
	}, [ data ]);

	
	if (isError) return (
		<p className='error-message'>{t('postErrorMessage', { ns: 'global' })}</p>
	)


	return (
		<Button className='app-form-button disable-selection' autoFocus={false} isDisabled={isLoading} onPress={handleSave}>
			{isLoading ? <LoadingSpinner /> : t('footer.finishOrder')}
		</Button>
	);
};

export default SaveButton;